import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Avatar,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import UpdateIcon from "@mui/icons-material/Update";
import ArticleIcon from "@mui/icons-material/Article";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 25px rgba(33, 150, 243, 0.6); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function AdminPostManagement() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletePostTitle, setDeletePostTitle] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Dropdown data - only child categories
  const [childCategories, setChildCategories] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    unitType: "kg",
    quantity: "",
    location: "",
    post_type: "sell",
    childCategory: "",
    user_name: "",
    user_mobile: "",
  });

  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removeImages, setRemoveImages] = useState([]);

  // Filter states
  const [filterTitle, setFilterTitle] = useState("");

  const token = localStorage.getItem("token");

  // Fetch all posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin-post/getAllPosts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPosts(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch posts", "error");
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      showSnackbar(error.response?.data?.message || "Error fetching posts", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Child Categories - always fetch all
  const fetchChildCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/childcategory/get-all-childcategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setChildCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching child categories:", error);
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchChildCategories(); // Always fetch all child categories on component mount
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = [...selectedImages, ...files];
      setSelectedImages(newImages);

      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const removeSelectedImage = (index) => {
    const newImages = [...selectedImages];
    newImages.splice(index, 1);
    setSelectedImages(newImages);

    const newPreviews = [...imagePreviews];
    URL.revokeObjectURL(newPreviews[index]);
    newPreviews.splice(index, 1);
    setImagePreviews(newPreviews);
  };

  const handleRemoveExistingImage = (publicId, index) => {
    setRemoveImages((prev) => [...prev, publicId]);
    const newExisting = [...existingImages];
    newExisting.splice(index, 1);
    setExistingImages(newExisting);
  };

  const handleOpenDialog = async (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setFormData({
        title: item.title || "",
        description: item.description || "",
        price: item.price || "",
        unitType: item.unitType || "kg",
        quantity: item.quantity || "",
        location: item.location || "",
        post_type: item.post_type || "sell",
        childCategory: item.childCategory?._id || item.childCategory || "",
        user_name: item.user?.name || "",
        user_mobile: item.user?.mobile_number || "",
      });
      setExistingImages(item.postImage || []);
      setRemoveImages([]);
      setSelectedImages([]);
      setImagePreviews([]);
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      unitType: "kg",
      quantity: "",
      location: "",
      post_type: "sell",
      childCategory: "",
      user_name: "",
      user_mobile: "",
    });
    setSelectedImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setRemoveImages([]);
    setSelectedId(null);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      showSnackbar("Please fill required fields", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });

      selectedImages.forEach((img) => {
        data.append("postImage", img);
      });

      if (formMode === "edit") {
        if (removeImages.length > 0) {
          data.append("removeImages", JSON.stringify(removeImages));
        }
      }

      let response;
      if (formMode === "add") {
        response = await axios.post(`${BASE_URL}/admin-post/createPost`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await axios.put(`${BASE_URL}/admin-post/updatePost?id=${selectedId}`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success) {
        showSnackbar(`Post ${formMode === "add" ? "created" : "updated"} successfully!`, "success");
        fetchPosts();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting post:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (id, title) => {
    setDeleteId(id);
    setDeletePostTitle(title);
    setDeleteDialogOpen(true);
  };

  const handleDeletePost = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${BASE_URL}/admin-post/deletePost?id=${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        showSnackbar("Post deleted successfully!", "success");
        fetchPosts();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to delete post", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPosts = useMemo(() => {
    if (!filterTitle.trim()) return posts;
    return posts.filter((post) => post.title?.toLowerCase().includes(filterTitle.toLowerCase()));
  }, [posts, filterTitle]);

  const columns = [
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       IMAGE
    //     </MDTypography>
    //   ),
    //   accessor: "image",
    //   align: "center",
    //   width: "10%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POST DETAILS
        </MDTypography>
      ),
      accessor: "title",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POSTED BY
        </MDTypography>
      ),
      accessor: "user",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CATEGORY
        </MDTypography>
      ),
      accessor: "category",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PRICE & QTY
        </MDTypography>
      ),
      accessor: "priceQty",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POST TYPE
        </MDTypography>
      ),
      accessor: "postType",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
    },
  ];

  const rows = useMemo(
    () =>
      filteredPosts.map((post) => ({
        image: (
          <Avatar
            src={post.postImage?.[0] || ""}
            alt={post.title}
            variant="rounded"
            sx={{ width: 45, height: 45, mx: "auto" }}
          >
            <ArticleIcon />
          </Avatar>
        ),
        title: (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold" display="block">
              {post.title}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              display="block"
              fontWeight="medium"
              sx={{ fontSize: "0.7rem" }}
            >
              ID: #{post._id?.slice(-5).toUpperCase()}
            </MDTypography>
          </MDBox>
        ),
        category: (
          <MDBox>
            <MDTypography variant="caption" fontWeight="medium" display="block" color="text">
              {post.childCategory?.name}
            </MDTypography>
          </MDBox>
        ),
        priceQty: (
          <MDBox textAlign="center">
            <MDTypography variant="button" fontWeight="bold" color="primary">
              ₹{post.price}/{post.unitType}
            </MDTypography>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
              Qty: {post.quantity} {post.unitType}
            </MDTypography>
          </MDBox>
        ),
        postType: (
          <MDBox textAlign="center">
            <Chip
              label={post.post_type?.toUpperCase()}
              size="small"
              color={post.post_type === "sell" ? "error" : "info"}
              variant="gradient"
            />
          </MDBox>
        ),
        user: (
          <MDBox>
            <MDTypography variant="caption" fontWeight="bold" display="block">
              {post.user?.name}
            </MDTypography>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {post.user?.mobile_number}
            </MDTypography>
          </MDBox>
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={0}>
            <Tooltip title="View Details">
              <IconButton
                color="info"
                onClick={() => navigate(`/admin-post-details/${post._id}`)}
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Post">
              <IconButton color="info" onClick={() => handleOpenDialog("edit", post)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Post">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(post._id, post.title)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredPosts]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "20px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <ArticleIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Admin Post Management
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="dark"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog("add")}
                >
                  Create Post
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Search by Title"
                      size="small"
                      value={filterTitle}
                      onChange={(e) => setFilterTitle(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() => {
                        setFilterTitle("");
                      }}
                      startIcon={<ClearIcon />}
                      fullWidth
                    >
                      Clear
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredPosts.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No posts found matching the filters.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Form Dialog */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(33,150,243,0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle sx={{ pb: 1, pt: 3 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33,150,243,0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  {formMode === "add" ? "Create Admin Post" : "Update Admin Post"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add" ? "Fill details to create a new post" : "Edit post details"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Mobile"
                    name="user_mobile"
                    value={formData.user_mobile}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Post Title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Post Type</InputLabel>
                    <Select
                      name="post_type"
                      value={formData.post_type}
                      onChange={handleInputChange}
                      label="Post Type"
                      sx={{ height: "44px", borderRadius: "12px" }}
                    >
                      <MenuItem value="sell">Sell</MenuItem>
                      <MenuItem value="buy">Buy</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Unit (e.g. kg, ton)"
                    name="unitType"
                    value={formData.unitType}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>

                {/* Category Dropdown - Always shows all child categories */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel
                      sx={{
                        fontSize: "0.8rem",
                        backgroundColor: "white",
                        px: 0.5,
                      }}
                    >
                      Category
                    </InputLabel>
                    <Select
                      name="childCategory"
                      value={formData.childCategory}
                      onChange={handleInputChange}
                      label="Category"
                      size="small"
                      sx={{
                        height: "38px",
                        borderRadius: "10px",
                        fontSize: "0.85rem",
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          transition: "all 0.2s ease",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2196f3",
                          borderWidth: "1.5px",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2196f3",
                          borderWidth: "2px",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "10px",
                            mt: 1,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            maxHeight: 300,
                            "& .MuiMenuItem-root": {
                              fontSize: "0.85rem",
                              py: 1,
                              px: 2,
                              transition: "all 0.2s",
                            },
                          },
                        },
                      }}
                    >
                      {childCategories.length === 0 ? (
                        <MenuItem disabled sx={{ fontSize: "0.85rem", color: "#999" }}>
                          No categories available
                        </MenuItem>
                      ) : (
                        childCategories.map((cat) => (
                          <MenuItem
                            key={cat._id}
                            value={cat._id}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(33, 150, 243, 0.08)",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "rgba(33, 150, 243, 0.12)",
                                fontWeight: 500,
                                "&:hover": {
                                  backgroundColor: "rgba(33, 150, 243, 0.16)",
                                },
                              },
                            }}
                          >
                            {cat.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <LocationOnIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>

                {/* Image Upload */}
                <Grid item xs={12}>
                  <MDBox>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color="medium"
                      mb={1}
                      display="block"
                      sx={{ fontSize: "0.9rem", letterSpacing: "0.5px" }}
                    >
                      Post Images
                    </MDTypography>

                    <MDBox
                      sx={{
                        border: "2px dashed #e0e0e0",
                        borderRadius: "16px",
                        p: 3,
                        bgcolor: "#fafafa",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#2196f3",
                          bgcolor: "rgba(33, 150, 243, 0.02)",
                        },
                      }}
                    >
                      <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
                        {/* Existing Images */}
                        {existingImages.map((img, index) => (
                          <MDBox
                            key={index}
                            position="relative"
                            sx={{
                              "&:hover": { transform: "scale(1.05)", transition: "transform 0.2s" },
                            }}
                          >
                            <Avatar
                              src={img}
                              variant="rounded"
                              sx={{
                                width: 100,
                                height: 100,
                                border: "2px solid #e0e0e0",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                objectFit: "cover",
                              }}
                            />
                            <Tooltip title="Remove image">
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  bgcolor: "#f44336",
                                  color: "#fff",
                                  width: 24,
                                  height: 24,
                                  "&:hover": {
                                    bgcolor: "#d32f2f",
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.2s",
                                }}
                                onClick={() => handleRemoveExistingImage(img, index)}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ mt: 1, display: "block", textAlign: "center" }}
                            >
                              Existing
                            </MDTypography>
                          </MDBox>
                        ))}

                        {/* New Image Previews */}
                        {imagePreviews.map((preview, index) => (
                          <MDBox
                            key={index}
                            position="relative"
                            sx={{
                              "&:hover": { transform: "scale(1.05)", transition: "transform 0.2s" },
                            }}
                          >
                            <Avatar
                              src={preview}
                              variant="rounded"
                              sx={{
                                width: 100,
                                height: 100,
                                border: "2px solid #2196f3",
                                boxShadow: "0 2px 8px rgba(33,150,243,0.2)",
                                objectFit: "cover",
                              }}
                            />
                            <Tooltip title="Remove image">
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  bgcolor: "#f44336",
                                  color: "#fff",
                                  width: 24,
                                  height: 24,
                                  "&:hover": {
                                    bgcolor: "#d32f2f",
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.2s",
                                }}
                                onClick={() => removeSelectedImage(index)}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <MDTypography
                              variant="caption"
                              color="primary"
                              sx={{ mt: 1, display: "block", textAlign: "center" }}
                            >
                              New
                            </MDTypography>
                          </MDBox>
                        ))}

                        {/* Upload Button */}
                        <label htmlFor="post-image-upload">
                          <input
                            accept="image/*"
                            id="post-image-upload"
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                          />
                          <MDBox
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: "12px",
                              border: "2px dashed #ccc",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              bgcolor: "white",
                              "&:hover": {
                                borderColor: "#2196f3",
                                bgcolor: "rgba(33,150,243,0.05)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <CloudUploadIcon sx={{ color: "#999", fontSize: 32, mb: 0.5 }} />
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ fontSize: "10px", textAlign: "center" }}
                            >
                              Upload
                            </MDTypography>
                          </MDBox>
                        </label>
                      </MDBox>

                      {/* Helper Text */}
                      <MDTypography
                        variant="caption"
                        color="textSecondary"
                        sx={{ mt: 2, display: "block", textAlign: "center" }}
                      >
                        Click to upload images (JPG, PNG, GIF) • Max 5MB each
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => setOpenForm(false)}
            disabled={isSubmitting}
            sx={{ borderRadius: "12px", textTransform: "none" }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
            sx={{ borderRadius: "12px", px: 4, textTransform: "none" }}
          >
            {isSubmitting ? "Processing..." : formMode === "add" ? "Create Post" : "Update Post"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(239,68,68,0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />
        {[...Array(15)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              background: `rgba(239,68,68,${Math.random() * 0.3 + 0.1})`,
              borderRadius: "50%",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `${float} ${Math.random() * 5 + 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              pointerEvents: "none",
            }}
          />
        ))}
        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239,68,68,0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 40, color: "#fff" }} />
                  </Box>
                </Box>
              </Zoom>
              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    display: "inline-block",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Post?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    "& .MuiAlert-icon": { color: "#ef4444" },
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>
              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239,68,68,0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "scale(1.02)", borderColor: "rgba(239,68,68,0.4)" },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    POST TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    {deletePostTitle}
                  </MDTypography>
                </Box>
              </Grow>
              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This post will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                borderWidth: "2px",
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  transform: "translateY(-2px)",
                  borderColor: "#ef4444",
                  backgroundColor: "rgba(239,68,68,0.05)",
                },
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeletePost}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "0 6px 14px rgba(239,68,68,0.4)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 20px rgba(239,68,68,0.5)",
                  backgroundColor: "#dc2626",
                },
                "&:active": { transform: "translateY(0)" },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                  transition: "left 0.5s ease",
                },
                "&:hover::before": { left: "100%" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "Deleting..." : "Delete Permanently"}
            </MDButton>
          </Zoom>
        </DialogActions>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default AdminPostManagement;
